<template>
  <FriedModal @close="handleClose">
    <template #header>
      <FriedModalHeader :header="$t('call-sender')" />
    </template>
    <div class="phone-modal-container">
      <p>{{ $t('add-your-phone-number-to-get-in-conntact-with-the-sender') }}</p>
      <PhoneNumberInput
        :disabled="calling"
        :label="$t('phone-number')"
        :default-country-code="entity.countryCode || 'SE'"
        data-external="call-sender-phone-input"
        :error-message="$t(phoneInputError)"
        :value="phoneNumber"
        class="phone-input"
        @update="handlePhoneInput($event)"
      />
      <p class="error">{{ error }}</p>
    </div>
    <template #footer>
      <FriedModalButtonsFooter>
        <FriedButton
          v-if="calling"
          data-external="call-sender-phone-cancel-button"
          :button-type="ButtonType.Secondary"
          @click="handleCancelCall"
          >{{ $t('cancel-call') }}
        </FriedButton>
        <FriedButton
          v-else
          data-external="call-sender-phone-connect-button"
          :disabled="callButtonDisabled"
          :loading="loading"
          @click="handleCallPhone"
          >{{ $t('connect-call') }}
        </FriedButton>
      </FriedModalButtonsFooter>
    </template>
  </FriedModal>
</template>

<script lang="ts">
import { ButtonType } from '@getaccept/fried-tofu';
import { testMobileNumber } from '@getaccept/lib-shared-new/src/helpers';
import PhoneNumberInput from '@getaccept/lib-shared-new/src/components/PhoneNumberInput.vue';
import type { Entity } from '@getaccept/lib-shared-new/src/entity/signing-site/types/entity';
import type { SigningPageRecipient } from '@getaccept/lib-shared-new/src/types/signing-page-recipient';
import type { PropType } from 'vue';
import { onMounted, computed, ref, defineComponent } from 'vue';
import { useTwilio } from '../composables/twilio.composable';

export default defineComponent({
  components: {
    PhoneNumberInput,
  },
  props: {
    recipient: { type: Object as PropType<SigningPageRecipient>, default: undefined },
    entity: { type: Object as PropType<Entity>, default: undefined },
  },
  setup(props, { emit }) {
    const { loading, callSid, error, cancelCall, callPhone } = useTwilio();

    const phoneInputError = ref('');
    const phoneNumber = ref('');

    const calling = computed(() => !!callSid.value);

    const callButtonDisabled = computed(
      () => !phoneNumber.value || !!phoneInputError.value || calling.value
    );

    const handlePhoneInput = (value: string) => {
      phoneNumber.value = value;
      if (!testMobileNumber(value)) {
        phoneInputError.value = 'enter-a-valid-phone-number';
      } else {
        phoneInputError.value = '';
      }
    };

    const handleClose = () => {
      emit('close');
    };

    const handleCancelCall = () => {
      cancelCall();
    };

    const handleCallPhone = () => {
      callPhone(phoneNumber.value);
    };

    onMounted(() => {
      phoneNumber.value = props.recipient?.mobile || '';
    });

    return {
      phoneInputError,
      phoneNumber,
      ButtonType,
      loading,
      callSid,
      error,
      calling,
      callButtonDisabled,
      handlePhoneInput,
      handleClose,
      handleCancelCall,
      handleCallPhone,
    };
  },
});
</script>

<style lang="scss" scoped>
.error {
  color: var(--error-dark);
  margin: var(--spacing-25) 0;
}

.phone-input {
  margin-top: var(--spacing-100);
}
</style>
